import { c as _c } from "react/compiler-runtime";
import { getOrgProxyConfigsOptions } from '@onefootprint/fetch-dashboard';
import type { ApiNodeDataExternalApi, NodeDataUpdateExternalApi } from '@onefootprint/request-types/dashboard';
import { Divider, Form, Select, type TemplateInputProps } from '@onefootprint/ui';
import { useQuery } from '@tanstack/react-query';
import Link from 'next/link';
import { useEffect } from 'react';
import { FormProvider, useFormContext, useWatch } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import useDirty from '@/components/playbook-builder/hooks/use-dirty';
import { useGetNodeConfigContext } from '@/components/playbook-builder/hooks/use-get-node-config-context';
import useOrgSession from '@/hooks/use-org-session';
import useSession from '@/hooks/use-session';
import type { ApplyEditMutation } from '../../../../../base-node';
import { type ExternalApiFormData, MOCK_RESPONSE_VALUE } from '../../../../types';
import { createOnSubmitHandler } from '../../utils/submit-handler';
import OnboardingVariables from '../onboarding-variables';
import Parameters from '../parameters';
import PathInput from '../path-input';
export type ExternalApiContentFormProps = {
  node: {
    id: string;
    data: ApiNodeDataExternalApi;
  };
  mutation: ApplyEditMutation;
  canEdit: boolean;
  onSubmit?: (data: NodeDataUpdateExternalApi) => void; // For storybook testing
};
const ExternalApiContentForm = t0 => {
  const $ = _c(167);
  const {
    node,
    mutation,
    canEdit,
    onSubmit
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "externalApi"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("playbook-builder", t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      keyPrefix: "codeBody"
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    t: tCodeBody
  } = useTranslation("playbook-builder", t2);
  const {
    data: session
  } = useSession();
  const {
    sandbox
  } = useOrgSession();
  const isSandboxRestricted = session.org?.isSandboxRestricted;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = getOrgProxyConfigsOptions({
      headers: {
        "x-is-live": "true"
      }
    });
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const t4 = !isSandboxRestricted;
  let t5;
  if ($[3] !== t4) {
    t5 = {
      ...t3,
      enabled: t4
    };
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const {
    data: proxyConfigs
  } = useQuery(t5);
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = getOrgProxyConfigsOptions({
      headers: {
        "x-is-live": "false"
      }
    });
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const {
    data: sandboxProxyConfigs
  } = useQuery(t6);
  const config = useGetNodeConfigContext(node.id, "templatable");
  const {
    toggle
  } = useDirty();
  const formMethods = useFormContext();
  if (!formMethods) {
    throw new Error("ExternalApiContentForm must be used within a FormProvider");
  }
  const {
    control,
    formState
  } = formMethods;
  const handleSubmit = createOnSubmitHandler(node, mutation, onSubmit);
  let t7;
  if ($[6] !== formState.isDirty || $[7] !== toggle) {
    t7 = () => {
      toggle("external-api-form", formState.isDirty);
    };
    $[6] = formState.isDirty;
    $[7] = toggle;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== formState.isDirty) {
    t8 = [formState.isDirty];
    $[9] = formState.isDirty;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  useEffect(t7, t8);
  let t9;
  if ($[11] !== control) {
    t9 = {
      control,
      name: "config.method"
    };
    $[11] = control;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const method = useWatch(t9);
  let t10;
  if ($[13] !== control) {
    t10 = {
      control,
      name: "config.sandboxProxyConfig"
    };
    $[13] = control;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  const sandboxProxyConfig = useWatch(t10);
  let t11;
  if ($[15] !== control) {
    t11 = {
      control,
      name: "config.useSandboxHttpProperties"
    };
    $[15] = control;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  const useSandboxHttpProperties = useWatch(t11);
  let t12;
  if ($[17] !== control) {
    t12 = {
      control,
      name: "config.sandboxHttpProperties.method"
    };
    $[17] = control;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  const sandboxMethod = useWatch(t12);
  const shouldShowBody = method !== "get";
  const isMockResponse = sandboxProxyConfig === MOCK_RESPONSE_VALUE;
  const hasSandboxProxy = !!sandboxProxyConfig && !isMockResponse;
  const shouldShowSandboxBody = sandboxMethod !== "get";
  let t13;
  if ($[19] !== control) {
    t13 = {
      control,
      name: "config.proxyConfig"
    };
    $[19] = control;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  const proxyConfigId = useWatch(t13);
  let t14;
  if ($[21] !== control) {
    t14 = {
      control,
      name: "config.sandboxProxyConfig"
    };
    $[21] = control;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  const sandboxProxyConfigId = useWatch(t14);
  let t15;
  if ($[23] !== proxyConfigId || $[24] !== proxyConfigs) {
    t15 = proxyConfigs?.find(pc => pc.id === proxyConfigId);
    $[23] = proxyConfigId;
    $[24] = proxyConfigs;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  const selectedLiveProxyConfig = t15;
  let t16;
  if ($[26] !== sandboxProxyConfigId || $[27] !== sandboxProxyConfigs) {
    t16 = sandboxProxyConfigs?.find(pc_0 => pc_0.id === sandboxProxyConfigId);
    $[26] = sandboxProxyConfigId;
    $[27] = sandboxProxyConfigs;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  const selectedSandboxProxyConfig = t16;
  const baseUrl = sandbox.isSandbox ? selectedSandboxProxyConfig?.url : selectedLiveProxyConfig?.url;
  let t17;
  if ($[29] !== config?.autocomplete) {
    t17 = Object.entries(config?.autocomplete || {}).reduce(_temp2, {} as TemplateInputProps['autocompleteItems']);
    $[29] = config?.autocomplete;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  const autocompleteItems = t17;
  const T0 = FormProvider;
  const t18 = "external-api-form";
  const t19 = "flex flex-col gap-4 pb-4";
  const t20 = formMethods.handleSubmit(handleSubmit);
  let t21;
  if ($[31] !== t) {
    t21 = t(_temp3);
    $[31] = t;
    $[32] = t21;
  } else {
    t21 = $[32];
  }
  let t22;
  if ($[33] !== t21) {
    t22 = <div className="text-label-2">{t21}</div>;
    $[33] = t21;
    $[34] = t22;
  } else {
    t22 = $[34];
  }
  let t23;
  if ($[35] !== t) {
    t23 = t(_temp4);
    $[35] = t;
    $[36] = t23;
  } else {
    t23 = $[36];
  }
  let t24;
  if ($[37] !== t) {
    t24 = t(_temp5);
    $[37] = t;
    $[38] = t24;
  } else {
    t24 = $[38];
  }
  let t25;
  if ($[39] !== t24) {
    t25 = {
      required: t24
    };
    $[39] = t24;
    $[40] = t25;
  } else {
    t25 = $[40];
  }
  let t26;
  if ($[41] !== autocompleteItems || $[42] !== t23 || $[43] !== t25) {
    t26 = <div className="w-full md:w-[50%]"><Form.TemplateInput name="config.name" className="md:w-[50%]" placeholder={t23} autocompleteItems={autocompleteItems} rules={t25} size="compact" /></div>;
    $[41] = autocompleteItems;
    $[42] = t23;
    $[43] = t25;
    $[44] = t26;
  } else {
    t26 = $[44];
  }
  let t27;
  if ($[45] !== t22 || $[46] !== t26) {
    t27 = <div className="flex flex-col gap-2 w-full md:flex-row md:justify-between md:items-center md:gap-0">{t22}{t26}</div>;
    $[45] = t22;
    $[46] = t26;
    $[47] = t27;
  } else {
    t27 = $[47];
  }
  let t28;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <Divider />;
    $[48] = t28;
  } else {
    t28 = $[48];
  }
  let t29;
  if ($[49] !== t) {
    t29 = t(_temp6);
    $[49] = t;
    $[50] = t29;
  } else {
    t29 = $[50];
  }
  let t30;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <Trans ns="playbook-builder" i18nKey={_temp7} components={{
      a: <Link href="/developers/vault-proxy" target="_blank" className="text-body-2 text-accent" />
    }} />;
    $[51] = t30;
  } else {
    t30 = $[51];
  }
  let t31;
  if ($[52] !== t) {
    t31 = t(_temp8);
    $[52] = t;
    $[53] = t31;
  } else {
    t31 = $[53];
  }
  let t32;
  if ($[54] !== proxyConfigs) {
    t32 = proxyConfigs?.map(_temp9) || [];
    $[54] = proxyConfigs;
    $[55] = t32;
  } else {
    t32 = $[55];
  }
  let t33;
  if ($[56] !== t31 || $[57] !== t32) {
    t33 = <Form.Select name="config.proxyConfig" label={t31} placeholder="--" size="compact" options={t32} />;
    $[56] = t31;
    $[57] = t32;
    $[58] = t33;
  } else {
    t33 = $[58];
  }
  let t34;
  if ($[59] !== t) {
    t34 = t(_temp10);
    $[59] = t;
    $[60] = t34;
  } else {
    t34 = $[60];
  }
  let t35;
  if ($[61] !== t) {
    t35 = t(_temp11);
    $[61] = t;
    $[62] = t35;
  } else {
    t35 = $[62];
  }
  let t36;
  if ($[63] !== t35) {
    t36 = {
      label: t35,
      value: MOCK_RESPONSE_VALUE
    };
    $[63] = t35;
    $[64] = t36;
  } else {
    t36 = $[64];
  }
  let t37;
  if ($[65] !== sandboxProxyConfigs || $[66] !== t36) {
    t37 = [t36, ...(sandboxProxyConfigs?.map(_temp12) || [])];
    $[65] = sandboxProxyConfigs;
    $[66] = t36;
    $[67] = t37;
  } else {
    t37 = $[67];
  }
  let t38;
  if ($[68] !== t34 || $[69] !== t37) {
    t38 = <Form.Select name="config.sandboxProxyConfig" label={t34} placeholder="--" size="compact" options={t37} />;
    $[68] = t34;
    $[69] = t37;
    $[70] = t38;
  } else {
    t38 = $[70];
  }
  let t39;
  if ($[71] !== isMockResponse || $[72] !== t) {
    t39 = isMockResponse && <Form.JsonEditor name="config.useMockData" placeholder={t(_temp13)} disableJsonSchemaValidation={true} />;
    $[71] = isMockResponse;
    $[72] = t;
    $[73] = t39;
  } else {
    t39 = $[73];
  }
  let t40;
  if ($[74] !== t29 || $[75] !== t33 || $[76] !== t38 || $[77] !== t39) {
    t40 = <Section title={t29} subtitle={t30}>{t33}{t38}{t39}</Section>;
    $[74] = t29;
    $[75] = t33;
    $[76] = t38;
    $[77] = t39;
    $[78] = t40;
  } else {
    t40 = $[78];
  }
  let t41;
  if ($[79] !== t) {
    t41 = t(_temp14);
    $[79] = t;
    $[80] = t41;
  } else {
    t41 = $[80];
  }
  let t42;
  if ($[81] !== t) {
    t42 = t(_temp15);
    $[81] = t;
    $[82] = t42;
  } else {
    t42 = $[82];
  }
  let t43;
  if ($[83] !== autocompleteItems || $[84] !== baseUrl) {
    t43 = <PathInput autocompleteItems={autocompleteItems} baseUrl={baseUrl} />;
    $[83] = autocompleteItems;
    $[84] = baseUrl;
    $[85] = t43;
  } else {
    t43 = $[85];
  }
  let t44;
  if ($[86] !== t) {
    t44 = t(_temp16);
    $[86] = t;
    $[87] = t44;
  } else {
    t44 = $[87];
  }
  let t45;
  if ($[88] !== autocompleteItems || $[89] !== t44) {
    t45 = <Parameters fieldName="config.headers" title={t44} autocompleteItems={autocompleteItems} />;
    $[88] = autocompleteItems;
    $[89] = t44;
    $[90] = t45;
  } else {
    t45 = $[90];
  }
  let t46;
  if ($[91] !== t) {
    t46 = t(_temp17);
    $[91] = t;
    $[92] = t46;
  } else {
    t46 = $[92];
  }
  let t47;
  if ($[93] !== autocompleteItems || $[94] !== t46) {
    t47 = <Parameters fieldName="config.queryParams" title={t46} autocompleteItems={autocompleteItems} />;
    $[93] = autocompleteItems;
    $[94] = t46;
    $[95] = t47;
  } else {
    t47 = $[95];
  }
  let t48;
  if ($[96] !== autocompleteItems || $[97] !== shouldShowBody || $[98] !== tCodeBody) {
    t48 = shouldShowBody && <Form.JsonEditor label={tCodeBody(_temp18)} name="config.body" autocompleteItems={autocompleteItems} allowTrailingCommas={true} disableJsonSchemaValidation={true} rules={{
      validate: {
        monacoValidation: _temp19
      }
    }} />;
    $[96] = autocompleteItems;
    $[97] = shouldShowBody;
    $[98] = tCodeBody;
    $[99] = t48;
  } else {
    t48 = $[99];
  }
  let t49;
  if ($[100] !== t43 || $[101] !== t45 || $[102] !== t47 || $[103] !== t48) {
    t49 = <div className="flex flex-col gap-4">{t43}{t45}{t47}{t48}</div>;
    $[100] = t43;
    $[101] = t45;
    $[102] = t47;
    $[103] = t48;
    $[104] = t49;
  } else {
    t49 = $[104];
  }
  let t50;
  if ($[105] !== t41 || $[106] !== t42 || $[107] !== t49) {
    t50 = <Section title={t41} subtitle={t42}>{t49}</Section>;
    $[105] = t41;
    $[106] = t42;
    $[107] = t49;
    $[108] = t50;
  } else {
    t50 = $[108];
  }
  let t51;
  if ($[109] !== autocompleteItems || $[110] !== hasSandboxProxy || $[111] !== selectedSandboxProxyConfig?.url || $[112] !== shouldShowSandboxBody || $[113] !== t || $[114] !== tCodeBody || $[115] !== useSandboxHttpProperties) {
    t51 = hasSandboxProxy && <Section title={t(_temp20)} subtitle={t(_temp21)}><Form.Toggle name="config.useSandboxHttpProperties" label={t(_temp22)} />{useSandboxHttpProperties && <div className="flex flex-col gap-4"><PathInput autocompleteItems={autocompleteItems} fieldPrefix="config.sandboxHttpProperties." baseUrl={selectedSandboxProxyConfig?.url} /><Parameters fieldName="config.sandboxHttpProperties.headers" title={t(_temp23)} autocompleteItems={autocompleteItems} /><Parameters fieldName="config.sandboxHttpProperties.queryParams" title={t(_temp24)} autocompleteItems={autocompleteItems} />{shouldShowSandboxBody && <Form.JsonEditor label={tCodeBody(_temp25)} name="config.sandboxHttpProperties.body" autocompleteItems={autocompleteItems} allowTrailingCommas={true} disableJsonSchemaValidation={true} rules={{
          validate: {
            monacoValidation: _temp26
          }
        }} />}</div>}</Section>;
    $[109] = autocompleteItems;
    $[110] = hasSandboxProxy;
    $[111] = selectedSandboxProxyConfig?.url;
    $[112] = shouldShowSandboxBody;
    $[113] = t;
    $[114] = tCodeBody;
    $[115] = useSandboxHttpProperties;
    $[116] = t51;
  } else {
    t51 = $[116];
  }
  let t52;
  if ($[117] !== t) {
    t52 = t(_temp27);
    $[117] = t;
    $[118] = t52;
  } else {
    t52 = $[118];
  }
  let t53;
  if ($[119] !== t) {
    t53 = t(_temp28);
    $[119] = t;
    $[120] = t53;
  } else {
    t53 = $[120];
  }
  let t54;
  if ($[121] !== t) {
    t54 = t(_temp29);
    $[121] = t;
    $[122] = t54;
  } else {
    t54 = $[122];
  }
  let t55;
  if ($[123] !== t54) {
    t55 = <div className="text-label-3 text-primary">{t54}</div>;
    $[123] = t54;
    $[124] = t55;
  } else {
    t55 = $[124];
  }
  let t56;
  if ($[125] !== t) {
    t56 = t(_temp30);
    $[125] = t;
    $[126] = t56;
  } else {
    t56 = $[126];
  }
  let t57;
  if ($[127] !== t56) {
    t57 = <div className="flex flex-col gap-2"><Form.Toggle name="config.requireSuccessStatusCode" label={t56} /></div>;
    $[127] = t56;
    $[128] = t57;
  } else {
    t57 = $[128];
  }
  let t58;
  if ($[129] !== t55 || $[130] !== t57) {
    t58 = <div className="flex flex-col gap-4">{t55}{t57}</div>;
    $[129] = t55;
    $[130] = t57;
    $[131] = t58;
  } else {
    t58 = $[131];
  }
  let t59;
  if ($[132] !== t) {
    t59 = t(_temp31);
    $[132] = t;
    $[133] = t59;
  } else {
    t59 = $[133];
  }
  let t60;
  if ($[134] !== t59) {
    t60 = <div className="text-label-3 text-primary">{t59}</div>;
    $[134] = t59;
    $[135] = t60;
  } else {
    t60 = $[135];
  }
  let t61;
  if ($[136] !== t) {
    t61 = t(_temp32);
    $[136] = t;
    $[137] = t61;
  } else {
    t61 = $[137];
  }
  let t62;
  if ($[138] !== t) {
    t62 = t(_temp33);
    $[138] = t;
    $[139] = t62;
  } else {
    t62 = $[139];
  }
  let t63;
  if ($[140] !== t62) {
    t63 = [{
      label: t62,
      value: "json"
    }];
    $[140] = t62;
    $[141] = t63;
  } else {
    t63 = $[141];
  }
  let t64;
  if ($[142] !== t61 || $[143] !== t63) {
    t64 = <Select aria-label={t61} value="json" options={t63} size="compact" disabled={true} onChange={_temp34} />;
    $[142] = t61;
    $[143] = t63;
    $[144] = t64;
  } else {
    t64 = $[144];
  }
  let t65;
  if ($[145] !== t60 || $[146] !== t64) {
    t65 = <div className="flex flex-col gap-2">{t60}{t64}</div>;
    $[145] = t60;
    $[146] = t64;
    $[147] = t65;
  } else {
    t65 = $[147];
  }
  let t66;
  if ($[148] !== canEdit) {
    t66 = <OnboardingVariables canEdit={canEdit} />;
    $[148] = canEdit;
    $[149] = t66;
  } else {
    t66 = $[149];
  }
  let t67;
  if ($[150] !== t52 || $[151] !== t53 || $[152] !== t58 || $[153] !== t65 || $[154] !== t66) {
    t67 = <Section title={t52} subtitle={t53}>{t58}{t65}{t66}</Section>;
    $[150] = t52;
    $[151] = t53;
    $[152] = t58;
    $[153] = t65;
    $[154] = t66;
    $[155] = t67;
  } else {
    t67 = $[155];
  }
  let t68;
  if ($[156] !== t20 || $[157] !== t27 || $[158] !== t40 || $[159] !== t50 || $[160] !== t51 || $[161] !== t67) {
    t68 = <form id={t18} className={t19} onSubmit={t20}>{t27}{t28}{t40}{t50}{t51}{t67}</form>;
    $[156] = t20;
    $[157] = t27;
    $[158] = t40;
    $[159] = t50;
    $[160] = t51;
    $[161] = t67;
    $[162] = t68;
  } else {
    t68 = $[162];
  }
  let t69;
  if ($[163] !== T0 || $[164] !== formMethods || $[165] !== t68) {
    t69 = <T0 {...formMethods}>{t68}</T0>;
    $[163] = T0;
    $[164] = formMethods;
    $[165] = t68;
    $[166] = t69;
  } else {
    t69 = $[166];
  }
  return t69;
};
const Section = t0 => {
  const $ = _c(10);
  const {
    title,
    subtitle,
    children
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = <div className="text-label-2 text-primary">{title}</div>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== subtitle) {
    t2 = <div className="text-body-2 text-tertiary">{subtitle}</div>;
    $[2] = subtitle;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="flex flex-col gap-0.5">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== children || $[8] !== t3) {
    t4 = <div className="p-3 border border-tertiary rounded-md flex flex-col gap-5">{t3}{children}</div>;
    $[7] = children;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
};
export default ExternalApiContentForm;
function _temp(item) {
  return {
    label: item.token,
    value: item.token,
    detail: item.dataType.join(" | ")
  };
}
function _temp2(acc, t0) {
  const [key, value] = t0;
  acc[key === "workflowContextKeys" ? "others" : key] = value.map(_temp);
  return acc;
}
function _temp3($) {
  return $.nodeName.label;
}
function _temp4($_0) {
  return $_0.nodeName.placeholder;
}
function _temp5($_1) {
  return $_1.nodeName.required;
}
function _temp6($_2) {
  return $_2.vaultProxyConfig.title;
}
function _temp7($_3) {
  return $_3.externalApi.vaultProxyConfig.subtitle;
}
function _temp8($_4) {
  return $_4.vaultProxyConfig.liveEnvironment;
}
function _temp9(t0) {
  const {
    id,
    name
  } = t0;
  return {
    label: name,
    value: id
  };
}
function _temp10($_5) {
  return $_5.vaultProxyConfig.sandboxEnvironment;
}
function _temp11($_6) {
  return $_6.vaultProxyConfig.useMockResponse;
}
function _temp12(t0) {
  const {
    id: id_0,
    name: name_0
  } = t0;
  return {
    label: name_0,
    value: id_0
  };
}
function _temp13($_7) {
  return $_7.mockResponse.placeholder;
}
function _temp14($_8) {
  return $_8.requestConfig.title;
}
function _temp15($_9) {
  return $_9.requestConfig.subtitle;
}
function _temp16($_10) {
  return $_10.requestConfig.headers;
}
function _temp17($_11) {
  return $_11.requestConfig.queryParameters;
}
function _temp18($_12) {
  return $_12.body;
}
function _temp19() {
  return true;
}
function _temp20($_13) {
  return $_13.sandboxRequestConfig.title;
}
function _temp21($_14) {
  return $_14.sandboxRequestConfig.subtitle;
}
function _temp22($_15) {
  return $_15.sandboxRequestConfig.toggle;
}
function _temp23($_16) {
  return $_16.requestConfig.headers;
}
function _temp24($_17) {
  return $_17.requestConfig.queryParameters;
}
function _temp25($_18) {
  return $_18.body;
}
function _temp26() {
  return true;
}
function _temp27($_19) {
  return $_19.responseConfig.title;
}
function _temp28($_20) {
  return $_20.responseConfig.subtitle;
}
function _temp29($_21) {
  return $_21.responseConfig.statusCode.title;
}
function _temp30($_22) {
  return $_22.responseConfig.statusCode.label;
}
function _temp31($_23) {
  return $_23.responseConfig.contentType.label;
}
function _temp32($_24) {
  return $_24.responseConfig.contentType.label;
}
function _temp33($_25) {
  return $_25.responseConfig.contentType.json;
}
function _temp34() {}